/**
 * Situações do relatório de conclusão da Renovação de GDM (base de GDM x solicitações) e as agregações da tela.
 * A situação de cada GDM vem pronta do backend (ConclusaoRenovacaoGdmService).
 */
export const SITUACOES = [
    { value: "RENOVADA", label: "Renovadas", singular: "Renovada", cor: "#10B981", icon: "fa fa-check-circle", iconClassName: "bg-success/10 text-success" },
    { value: "NAO_RENOVADA", label: "Não renovadas", singular: "Não renovada", cor: "#EF4444", icon: "fa fa-times-circle", iconClassName: "bg-destructive/10 text-destructive" },
    { value: "REPROVADA_CANCELADA", label: "Reprovadas / canceladas", singular: "Reprovada / cancelada", cor: "#94A3B8", icon: "fa fa-ban", iconClassName: "bg-muted text-muted-foreground" },
    { value: "EM_ANDAMENTO", label: "Em andamento", singular: "Em andamento", cor: "#3B82F6", icon: "fa fa-spinner", iconClassName: "bg-primary/10 text-primary" },
    { value: "SEM_SOLICITACAO", label: "Sem solicitação", singular: "Sem solicitação", cor: "#F59E0B", icon: "fa fa-hourglass-half", iconClassName: "bg-amber-100 text-amber-700" },
];

export const SITUACAO_POR_VALOR = Object.fromEntries(SITUACOES.map(s => [s.value, s]));

/** Situações em que o processo terminou (com ou sem renovação). */
export const SITUACOES_CONCLUIDAS = ["RENOVADA", "NAO_RENOVADA", "REPROVADA_CANCELADA"];

export const AGRUPAMENTOS = [
    // `ordem: "nome"`: grupos na ordem do próprio valor (rotas são números: 9, 10, 104...), e não pelo total.
    { value: "rota", label: "Rota", ordem: "nome" },
    { value: "cidade", label: "Cidade" },
    { value: "canal", label: "Canal" },
    { value: "logomarca", label: "Logomarca" },
    { value: "gdmStatus", label: "GDM Status" },
];

const SEM_VALOR = "(não informado)";

const percentual = (parte, total) => (total ? Math.round((parte / total) * 1000) / 10 : 0);

/** Contagem por situação + % concluído (processo encerrado) e % renovado, sobre o total de GDMs. */
export function resumir(gdms = []) {
    const contagem = Object.fromEntries(SITUACOES.map(s => [s.value, 0]));
    gdms.forEach(g => { contagem[g.situacao] = (contagem[g.situacao] || 0) + 1; });
    const total = gdms.length;
    const concluidas = SITUACOES_CONCLUIDAS.reduce((soma, s) => soma + contagem[s], 0);
    return {
        total,
        contagem,
        concluidas,
        percentualConcluido: percentual(concluidas, total),
        percentualRenovado: percentual(contagem.RENOVADA, total),
    };
}

/**
 * Agrupa as GDMs pelo campo informado (rota, cidade...). Cada linha: { grupo, total, RENOVADA, ..., concluidas,
 * percentualConcluido, percentualRenovado }. Ordem: pelo total (maior primeiro) ou, com `ordem: "nome"`, pelo valor do
 * grupo em ordem natural ("9" antes de "10"). "(não informado)" fica sempre por último.
 */
export function agrupar(gdms = [], campo, { ordem = "total" } = {}) {
    const grupos = new Map();
    gdms.forEach(g => {
        const chave = (g[campo] ?? "").toString().trim() || SEM_VALOR;
        if (!grupos.has(chave)) grupos.set(chave, []);
        grupos.get(chave).push(g);
    });
    return [...grupos.entries()]
        .map(([grupo, itens]) => {
            const r = resumir(itens);
            return { grupo, ...r.contagem, total: r.total, concluidas: r.concluidas, percentualConcluido: r.percentualConcluido, percentualRenovado: r.percentualRenovado };
        })
        .sort((a, b) => (a.grupo === SEM_VALOR) - (b.grupo === SEM_VALOR)
            || (ordem === "nome" ? 0 : b.total - a.total)
            || a.grupo.localeCompare(b.grupo, "pt-BR", { numeric: true }));
}

/**
 * GDMs por etapa da solicitação que define a situação delas: uma barra por etapa do fluxo (na ordem de `etapas`,
 * mesmo com zero), depois "Concluída" (processo encerrado, com ou sem renovação) e "Sem solicitação". GDMs em
 * andamento numa etapa fora da lista caem em "Etapa não informada" (só aparece se houver alguma).
 */
export function contarPorEtapa(gdms = [], etapas = []) {
    const barras = [
        ...etapas.map(e => ({ chave: e.codigo, label: e.label, total: 0 })),
        { chave: "CONCLUIDA", label: "Concluída", total: 0 },
        { chave: "SEM_SOLICITACAO", label: "Sem solicitação", total: 0 },
    ];
    const outra = { chave: "OUTRA", label: "Etapa não informada", total: 0 };
    const porChave = Object.fromEntries([...barras, outra].map(b => [b.chave, b]));
    gdms.forEach(g => { porChave[chaveEtapa(g, etapas)].total++; });
    return outra.total > 0 ? [...barras, outra] : barras;
}

/** Em qual barra de {@link contarPorEtapa} a GDM entra — usado também para filtrar a tabela ao clicar no gráfico. */
export function chaveEtapa(g, etapas = []) {
    if (g.situacao === "SEM_SOLICITACAO") return "SEM_SOLICITACAO";
    if (SITUACOES_CONCLUIDAS.includes(g.situacao)) return "CONCLUIDA";
    return etapas.some(e => e.codigo === g.etapaAtual) ? g.etapaAtual : "OUTRA";
}
